import React from "react";
import { MapPin, CheckCircle, Phone, ArrowRight, Navigation, ShieldCheck } from "lucide-react";
import { Button } from "./ui/button";
import { useQuoteModal } from "@/hooks/useQuoteModal";

const davieZipCodes = [
  { code: "33314", area: "Davie East / Davie Rd & Nova Southeastern Univ" },
  { code: "33324", area: "Davie Central / Pine Island Ridge & University Dr" },
  { code: "33328", area: "Davie / Shenandoah & Orange Drive" },
  { code: "33330", area: "Davie West / Forest Ridge & Griffin Rd" },
  { code: "33331", area: "Davie West / Hawkes Bluff & Flamingo Rd" }
];

const nearbyCities = [
  "Davie, FL",
  "Plantation",
  "Cooper City",
  "Weston",
  "Pembroke Pines",
  "Hollywood",
  "Fort Lauderdale",
  "Sunrise",
  "Miramar",
  "Southwest Ranches",
  "Coral Springs",
  "Pompano Beach",
  "Deerfield Beach",
  "Coconut Creek",
  "Tamarac",
  "Boca Raton"
];

export const DavieAreaSection: React.FC = () => {
  const { openModal } = useQuoteModal();
  return (
    <section id="davie-area" className="py-20 bg-slate-950 text-white relative overflow-hidden">
      {/* Background radial overlays */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-amber-400/10 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Section Title */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 bg-amber-400 text-slate-950 font-black px-4 py-1.5 rounded-full text-xs uppercase tracking-widest mb-4">
            <Navigation className="w-4 h-4" /> Davie FL Local Service Radius
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black font-heading uppercase tracking-tight mb-4">
            Junk Removal in <span className="text-amber-400">Davie, FL</span> & All of Broward County
          </h2>
          <p className="text-slate-300 text-base sm:text-lg">
            Headquartered right here in Davie! We provide junk removal services across Davie, Fort Lauderdale, Hollywood, Plantation, Cooper City, Sunrise & all surrounding cities within a 30-mile radius.
          </p>
        </div>

        {/* Davie Zip Codes Grid */}
        <div className="mb-14 bg-slate-900 border-2 border-amber-400/30 rounded-3xl p-6 sm:p-8">
          <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-800">
            <MapPin className="w-6 h-6 text-primary shrink-0" />
            <div>
              <h3 className="text-xl font-black font-heading uppercase text-white">
                Primary Davie Zip Code Coverage
              </h3>
              <p className="text-xs text-slate-400">
                Same-day junk removal available across all Davie neighborhoods
              </p>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {davieZipCodes.map((zip) => (
              <div
                key={zip.code}
                className="bg-slate-950 border border-slate-800 hover:border-amber-400 rounded-xl p-4 transition-colors flex items-start gap-3"
              >
                <div className="w-10 h-10 rounded-lg bg-amber-400/10 border border-amber-400/30 text-amber-400 font-black flex items-center justify-center shrink-0 text-sm">
                  {zip.code}
                </div>
                <div>
                  <div className="text-sm font-extrabold text-white">Zip Code {zip.code}</div>
                  <div className="text-xs text-slate-400">{zip.area}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 30-Mile Radius Cities List */}
        <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-black font-heading uppercase text-white mb-2">
              All Surrounding Broward & South Florida Service Cities
            </h3>
            <p className="text-slate-400 text-sm">
              Don't see your exact street? If you're in South Florida, we've got you covered!
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mb-8">
            {nearbyCities.map((city, index) => (
              <div
                key={index}
                className={`flex items-center gap-2 rounded-xl px-4 py-3 text-sm font-bold transition-all border ${
                  city === "Davie, FL"
                    ? "bg-amber-400 text-slate-950 border-amber-400 shadow-lg font-black"
                    : "bg-slate-950 text-slate-200 border-slate-800 hover:border-primary"
                }`}
              >
                <CheckCircle className={`w-4 h-4 shrink-0 ${city === "Davie, FL" ? "text-slate-950" : "text-amber-400"}`} />
                <span>{city}</span>
              </div>
            ))}
          </div>

          <div className="bg-slate-950 border border-slate-800 rounded-2xl p-6 text-center flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-left">
              <div className="text-base font-extrabold text-white">Need Urgent Same-Day Junk Pickup in Davie?</div>
              <div className="text-xs text-slate-400">Our trucks are dispatched locally daily across Griffin Rd, University Dr, Stirling Rd & I-595.</div>
            </div>
            <div className="flex gap-3 w-full sm:w-auto shrink-0">
              <a href="tel:9542808090" className="w-full sm:w-auto">
                <Button className="w-full bg-amber-400 hover:bg-amber-500 text-slate-950 font-extrabold uppercase shadow-lg shadow-amber-500/30">
                  <Phone className="w-4 h-4 mr-2" /> (954) 280-8090
                </Button>
              </a>
              <Button onClick={openModal} className="w-full !bg-rose-600 hover:!bg-rose-700 text-white font-extrabold uppercase shadow-lg shadow-rose-500/30 border-0">
                  Free Quote <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
            </div>
          </div>

        </div>

      </div>
    </section>
  );
};
